import React, { useSyncExternalStore } from 'react';
import { Circle, Group, Line, Rect, Text as KonvaText } from 'react-konva';
import type { DerivedGateModule } from '../../utils/derivedGateEquipment';
import { liveOhtRuntimeStore } from '../../store/liveOhtRuntimeStore';
import { useAppSelector } from '../../hooks/redux';
import { useLiveInventoryGates } from '../../hooks/useLiveInventoryDisplay';
import { EQUIPMENT_TRANSFER_FRACS as F } from './amhs/Equipment/equipmentPortTransfer';

/** Badge depth (extends away from track); connector runs +X toward the node pin. */
const BADGE_DEPTH = 8.5;
const BADGE_THICK = 9;
const HALF_T = BADGE_THICK * 0.5;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const lerp = (a: number, b: number, t: number) => a + (b - a) * clamp01(t);

const PALETTE = {
  entry: {
    fill: '#ECFDF5',
    border: '#047857',
    rail: '#10B981',
    chip: '#059669',
    glow: 'rgba(16, 185, 129, 0.35)',
  },
  exit: {
    fill: '#FFF1F2',
    border: '#BE123C',
    rail: '#F43F5E',
    chip: '#E11D48',
    glow: 'rgba(244, 63, 94, 0.35)',
  },
} as const;

const useRuntimeFrame = () =>
  useSyncExternalStore(
    (notify) => liveOhtRuntimeStore.subscribe(() => notify()),
    () => liveOhtRuntimeStore.getFrame(),
    () => liveOhtRuntimeStore.getFrame()
  );

function localNode(
  module: DerivedGateModule,
  groupX: number,
  groupY: number
): { x: number; y: number } {
  const radians = (-module.headingDeg * Math.PI) / 180;
  const dx = module.x - groupX;
  const dy = module.y - groupY;
  return {
    x: Math.cos(radians) * dx - Math.sin(radians) * dy,
    y: Math.sin(radians) * dx + Math.cos(radians) * dy,
  };
}

function foupPoint(
  role: DerivedGateModule['role'],
  progress: number,
  storage: { x: number; y: number },
  port: { x: number; y: number },
  node: { x: number; y: number }
): { x: number; y: number } | null {
  const p = clamp01(progress);
  if (role === 'entry') {
    if (p < F.pickupShutterOpenEnd) return storage;
    if (p < 0.6) {
      const u =
        (p - F.pickupShutterOpenEnd) /
        (0.6 - F.pickupShutterOpenEnd);
      return { x: lerp(storage.x, port.x, u), y: lerp(storage.y, port.y, u) };
    }
    if (p < F.pickupOhtTake) {
      const u = (p - 0.6) / (F.pickupOhtTake - 0.6);
      return { x: lerp(port.x, node.x, u), y: lerp(port.y, node.y, u) };
    }
    return null;
  }
  if (p < F.dropOhtRelease) return null;
  if (p < 0.55) {
    const u = (p - F.dropOhtRelease) / (0.55 - F.dropOhtRelease);
    return { x: lerp(node.x, port.x, u), y: lerp(node.y, port.y, u) };
  }
  if (p < F.dropFoupInside) {
    const u = (p - 0.55) / (F.dropFoupInside - 0.55);
    return { x: lerp(port.x, storage.x, u), y: lerp(port.y, storage.y, u) };
  }
  return storage;
}

export const DerivedGateEquipment2D: React.FC<{
  module: DerivedGateModule;
  groupX: number;
  groupY: number;
  zoomLevel: number;
  selected?: boolean;
}> = ({ module, groupX, groupY, zoomLevel, selected = false }) => {
  const frame = useRuntimeFrame();
  const running = useAppSelector((state) => state.simulation.running);
  const liveGate = useLiveInventoryGates()?.find(
    (gate) => gate.portId === module.loadPortId
  );
  const runtime = frame.gateByPortId[module.loadPortId];
  const node = localNode(module, groupX, groupY);
  const nodeDist = Math.hypot(node.x, node.y);
  const dockDeg = nodeDist > 1e-6 ? (Math.atan2(node.y, node.x) * 180) / Math.PI : 0;

  const dockNode = { x: nodeDist, y: 0 };
  const dockPort = { x: nodeDist * 0.42, y: 0 };
  const dockStorage = { x: -BADGE_DEPTH * 0.55, y: 0 };

  const active = running && runtime?.status === 'TRANSFERRING';
  const point = active
    ? foupPoint(module.role, runtime.progress01, dockStorage, dockPort, dockNode)
    : module.role === 'entry' ||
        (liveGate?.delivered ?? liveGate?.occupancy ?? 0) > 0
      ? dockStorage
      : null;

  const entry = module.role === 'entry';
  const theme = entry ? PALETTE.entry : PALETTE.exit;
  const status = runtime?.status ??
    (entry ? 'READY_FOR_PICKUP' : 'READY_FOR_DROP');
  const ledFill =
    status === 'TRANSFERRING'
      ? '#22D3EE'
      : point
        ? '#34D399'
        : '#94A3B8';

  const z = Math.max(zoomLevel, 0.08);
  const sw = (selected ? 2 : 1.25) / z;
  const pinR = 2.6 / z;
  const fontLabel = 3.4 / z;
  const fontTag = 2.5 / z;
  const r = 2 / z;

  const badgeLeft = -BADGE_DEPTH;
  const badgeRight = -0.35;

  return (
    <Group rotation={module.headingDeg}>
      <Group rotation={dockDeg}>
        {/* Rail link */}
        <Line
          points={[badgeRight, 0, nodeDist, 0]}
          stroke={theme.rail}
          strokeWidth={1.35 / z}
          lineCap="round"
          listening={false}
        />
        <Circle
          x={nodeDist}
          y={0}
          radius={pinR}
          fill="#FFFFFF"
          stroke={theme.border}
          strokeWidth={1.35 / z}
          listening={false}
        />
        <Circle
          x={nodeDist}
          y={0}
          radius={pinR * 0.45}
          fill={theme.rail}
          listening={false}
        />

        {selected && (
          <Rect
            x={badgeLeft - 0.8}
            y={-HALF_T - 0.8}
            width={BADGE_DEPTH + nodeDist + 1.6}
            height={BADGE_THICK + 1.6}
            stroke="#2563EB"
            strokeWidth={1.6 / z}
            cornerRadius={2.5 / z}
            listening={false}
          />
        )}

        {/* Gate body — always sits on the track-outside side (-X) */}
        <Rect
          x={badgeLeft}
          y={-HALF_T}
          width={badgeRight - badgeLeft}
          height={BADGE_THICK}
          fill={theme.fill}
          stroke={theme.border}
          strokeWidth={sw}
          cornerRadius={r}
          shadowColor={theme.glow}
          shadowBlur={selected ? 6 / z : 3 / z}
          shadowOpacity={0.9}
          listening={false}
        />
        <Rect
          x={badgeLeft}
          y={-HALF_T}
          width={1.6}
          height={BADGE_THICK}
          fill={theme.chip}
          listening={false}
        />

        {/* Flow arrow toward track */}
        <Line
          points={[
            -1.8,
            0,
            0.6,
            0,
            -0.15,
            -1.05,
            -0.15,
            1.05,
            0.6,
            0,
          ]}
          stroke={theme.border}
          strokeWidth={1 / z}
          lineJoin="round"
          lineCap="round"
          listening={false}
        />

        <KonvaText
          x={badgeLeft + 1.8}
          y={-HALF_T + 1.1}
          width={BADGE_DEPTH - 2.4}
          text={entry ? 'ENTRY' : 'EXIT'}
          align="center"
          fontSize={fontLabel}
          fontStyle="bold"
          fill={theme.border}
          listening={false}
        />
        <KonvaText
          x={badgeLeft + 1.8}
          y={HALF_T - fontTag - 1.4}
          width={BADGE_DEPTH - 2.4}
          text={entry ? 'FOUP IN' : 'FOUP OUT'}
          align="center"
          fontSize={fontTag}
          fill="#64748B"
          listening={false}
        />

        <Circle
          x={badgeLeft + 1.5}
          y={-HALF_T + 1.5}
          radius={0.95 / z}
          fill={ledFill}
          stroke={theme.border}
          strokeWidth={0.45 / z}
          listening={false}
        />

        {point && (
          <Rect
            x={point.x - 1.35}
            y={point.y - 1.35}
            width={2.7}
            height={2.7}
            fill="#6EC4E8"
            stroke={theme.border}
            strokeWidth={0.55 / z}
            cornerRadius={0.45 / z}
            listening={false}
          />
        )}
      </Group>
    </Group>
  );
};

export default React.memo(DerivedGateEquipment2D);
